import type { FilterCondition, FilterGroup, ColDef } from '../types';
import { getFilterRoot, getColumnType, canAddGroup } from '../state/filter-state';
import { getColumnDefs } from '../state/app-state';
import { getOperatorsForType } from '../config/filter-operators';
import { escapeHtml } from '../utils/dom-utils';

// Callbacks stored for potential future extensions
export function setFilterCallbacks(_onChange: () => void, _onRerender: () => void): void {
  // Callbacks available for future use
}

// Make functions available globally for inline handlers
declare global {
  interface Window {
    filterActions: {
      updateNode: (id: number | 'root', field: string, value: string) => void;
      removeNode: (id: number) => void;
      addCondition: (parentId: number | 'root') => void;
      addGroup: (parentId: number | 'root') => void;
      toggleMatchMode: (id: number | 'root') => void;
    };
  }
}

function renderCondition(condition: FilterCondition): string {
  const columnType = getColumnType(condition.column);
  const operators = getOperatorsForType(columnType);
  const columnDefs = getColumnDefs();

  const columnOptions = columnDefs.map(col =>
    `<option value="${escapeHtml(col.field as string)}" ${col.field === condition.column ? 'selected' : ''}>${escapeHtml(col.headerName as string)}</option>`
  ).join('');

  const operatorOptions = operators.map(op =>
    `<option value="${op.value}" ${op.value === condition.operator ? 'selected' : ''}>${escapeHtml(op.label)}</option>`
  ).join('');

  const showBetween = condition.operator === 'between';
  const showAnyOf = condition.operator === 'anyOf' || condition.operator === 'notAnyOf';
  const showValueInput = !condition.operator.includes('empty');

  let valueInputHtml = '';
  if (showValueInput) {
    if (showAnyOf) {
      valueInputHtml = `
        <div class="condition-row" style="flex-direction: column;">
          <textarea class="anyof-input"
                    placeholder="Werte eingeben (durch Komma oder Zeilenumbruch getrennt)"
                    oninput="window.filterActions.updateNode(${condition.id}, 'value', this.value)"
                    onchange="window.filterActions.updateNode(${condition.id}, 'value', this.value)">${escapeHtml(condition.value || '')}</textarea>
          <div class="anyof-hint">z.B. 20146, 20148, 20149</div>
        </div>
      `;
    } else if (showBetween) {
      valueInputHtml = `
        <div class="condition-row">
          <input type="number"
                 class="value-input number-input"
                 placeholder="Min"
                 value="${escapeHtml(condition.value)}"
                 oninput="window.filterActions.updateNode(${condition.id}, 'value', this.value)"
                 onchange="window.filterActions.updateNode(${condition.id}, 'value', this.value)"
                 step="0.01">
          <input type="number"
                 class="value-input number-input"
                 placeholder="Max"
                 value="${escapeHtml(condition.value2)}"
                 oninput="window.filterActions.updateNode(${condition.id}, 'value2', this.value)"
                 onchange="window.filterActions.updateNode(${condition.id}, 'value2', this.value)"
                 step="0.01">
        </div>
      `;
    } else {
      valueInputHtml = `
        <div class="condition-row">
          <input type="${columnType === 'number' ? 'number' : 'text'}"
                 class="value-input ${columnType === 'number' ? 'number-input' : ''}"
                 placeholder="Wert"
                 value="${escapeHtml(condition.value)}"
                 oninput="window.filterActions.updateNode(${condition.id}, 'value', this.value)"
                 onchange="window.filterActions.updateNode(${condition.id}, 'value', this.value)"
                 step="${columnType === 'number' ? '0.01' : ''}">
        </div>
      `;
    }
  }

  return `
    <div class="filter-condition" data-id="${condition.id}">
      <button class="remove-condition" onclick="window.filterActions.removeNode(${condition.id})">&times;</button>
      <div class="condition-row">
        <select class="column-select" onchange="window.filterActions.updateNode(${condition.id}, 'column', this.value)">
          ${columnOptions}
        </select>
        <select class="operator-select" onchange="window.filterActions.updateNode(${condition.id}, 'operator', this.value)">
          ${operatorOptions}
        </select>
      </div>
      ${valueInputHtml}
    </div>
  `;
}

function renderGroup(group: FilterGroup, isRoot: boolean = false): string {
  const matchModeClass = group.matchMode === 'ALL' ? 'and-mode' : 'or-mode';
  const matchModeLabel = group.matchMode === 'ALL' ? 'UND' : 'ODER';
  const connectorClass = group.matchMode === 'ALL' ? 'and-connector' : 'or-connector';
  const connectorLabel = group.matchMode === 'ALL' ? 'UND' : 'ODER';

  const canAddNestedGroup = canAddGroup(group.id);

  let childrenHtml = '';
  if (group.children.length === 0) {
    childrenHtml = `
      <div class="empty-conditions">
        Keine Filter. Klicken Sie auf "Bedingung", um zu starten.
      </div>
    `;
  } else {
    group.children.forEach((child, index) => {
      if (index > 0) {
        childrenHtml += `
          <div class="logic-connector ${connectorClass}">
            <span>${connectorLabel}</span>
          </div>
        `;
      }

      if (child.type === 'condition') {
        childrenHtml += renderCondition(child);
      } else if (child.type === 'group') {
        childrenHtml += renderGroup(child, false);
      }
    });
  }

  const groupIdStr = typeof group.id === 'string' ? `'${group.id}'` : group.id;

  if (isRoot) {
    return `
      <div class="filter-group" data-id="${group.id}">
        <div class="filter-group-header">
          <button class="match-toggle ${matchModeClass}" onclick="window.filterActions.toggleMatchMode(${groupIdStr})">
            ${matchModeLabel}
          </button>
          <span class="group-label">Alle Bedingungen verknüpfen mit</span>
        </div>
        <div class="filter-group-children">
          ${childrenHtml}
        </div>
        <div class="filter-group-actions">
          <button onclick="window.filterActions.addCondition(${groupIdStr})">+ Bedingung</button>
          <button onclick="window.filterActions.addGroup(${groupIdStr})" ${!canAddNestedGroup ? 'disabled title="Maximale Verschachtelungstiefe erreicht"' : ''}>+ Gruppe</button>
        </div>
      </div>
    `;
  }

  return `
    <div class="filter-group nested" data-id="${group.id}">
      <div class="filter-group-header">
        <button class="match-toggle ${matchModeClass}" onclick="window.filterActions.toggleMatchMode(${group.id})">
          ${matchModeLabel}
        </button>
        <span class="group-label">Untergruppe</span>
        <button class="remove-group" onclick="window.filterActions.removeNode(${group.id})">&times;</button>
      </div>
      <div class="filter-group-children">
        ${childrenHtml}
      </div>
      <div class="filter-group-actions">
        <button onclick="window.filterActions.addCondition(${group.id})">+ Bedingung</button>
        <button onclick="window.filterActions.addGroup(${group.id})" ${!canAddNestedGroup ? 'disabled title="Maximale Verschachtelungstiefe erreicht"' : ''}>+ Gruppe</button>
      </div>
    </div>
  `;
}

export function renderFilterTree(): void {
  const container = document.getElementById('filterConditions');
  if (!container) return;
  container.innerHTML = renderGroup(getFilterRoot(), true);
}

export function initFilterBuilder(_colDefs: ColDef[]): void {
  renderFilterTree();
}
